import type { SituacaoDoLote } from "@plantus/api-client";
import type { Situacao } from "@plantus/ui";

/**
 * O vocabulário de `SituacaoDoLote` traduzido para a tela.
 *
 * <h2>Por que existe</h2>
 *
 * A tela de lotes passava `l.situacao` — o valor cru do enum do servidor —
 * direto para o `<Selo>`, com o tom fixo em `"vencendo"`. Dois defeitos, e o
 * segundo é o grave:
 *
 * <ol>
 *   <li>`QUARENTENA` em caixa alta é vocabulário de banco de dados. Quem lê a
 *       tela é quem confere material no chão de fábrica, não quem escreveu a
 *       migration.</li>
 *   <li><b>A cor não acompanhava o dado.</b> Um lote `APROVADO` sairia com a
 *       cor de "vencendo" — a interface afirmando, em cor, o contrário do que
 *       o dado diz. Num sistema onde a cor do status decide o que se faz com o
 *       material, isso é a interface mentindo (ADR-0004: status de material
 *       precisa ser inequívoco).</li>
 * </ol>
 *
 * Hoje isso passa despercebido porque todo lote está em quarentena — não há
 * caminho para sair dela sem assinatura. O defeito não é teórico por isso: ele
 * é <b>silencioso</b>, e aparece pronto no dia em que a liberação existir.
 *
 * <h2>Onde mora</h2>
 *
 * No setor, não em `packages/ui`. O `Selo` não pode conhecer o enum da
 * Produção, e quem escolhe a palavra que aparece no selo tem de ser quem
 * desenhou a tela. Mesmo padrão do `ROTULO`/`TOM` de `PaginaFornecedores`; aqui
 * ele ganhou arquivo próprio porque duas telas do módulo o consomem — a lista
 * de lotes e a etiqueta —, e mapa duplicado é mapa que diverge.
 *
 * <h2>O `Record` é metade do controle</h2>
 *
 * `Record<SituacaoDoLote, …>` obriga os dois mapas a cobrir <b>todos</b> os
 * valores: acrescentar um estado ao enum sem decidir rótulo e cor quebra o
 * `typecheck`, em vez de produzir um selo sem cor em produção. A outra metade
 * é `situacaoDoLote.teste.tsx`, que prova que a cor sai diferente na tela.
 */

/**
 * A palavra que a pessoa lê. Vem do [glossário](docs/glossario.md) — a mesma
 * que está impressa na etiqueta física da prateleira.
 */
export const ROTULO_DA_SITUACAO: Record<SituacaoDoLote, string> = {
  QUARENTENA: "Quarentena",
  APROVADO: "Aprovado",
  REPROVADO: "Reprovado",
  BLOQUEADO: "Bloqueado",
  CONSUMIDO: "Consumido",
};

/**
 * A cor. **Verde é o único que autoriza usar o material** — e é por isso que
 * `APROVADO` está sozinho em `vigente`.
 *
 * <p>As cores saem da paleta de domínio (`packages/tema/src/dominio.css`), que
 * nenhum setor pode redefinir. Três delas batem token a token com o status de
 * material que a fábrica já usa:
 *
 * <pre>
 *   QUARENTENA  âmbar     --status-quarentena  ≡ --situacao-vencendo
 *   APROVADO    verde     --status-aprovado    ≡ --situacao-vigente
 *   REPROVADO   vermelho  --status-reprovado   ≡ --situacao-vencido
 *   BLOQUEADO   cinza     --status-bloqueado   ≈ --situacao-isento (um tom acima)
 *   CONSUMIDO   cinza     — sem token de domínio
 * </pre>
 *
 * <p>⚠️ **O nome do tom vem do vocabulário errado, e isso está registrado.** O
 * `Selo` só expõe a paleta *regulatória* (vigente · vencendo · vencido · em
 * obtenção · isento), que responde "este documento cobre esta operação?" — e o
 * `dominio.css` diz, com todas as letras, que ela não deve se misturar com o
 * status de material, que responde "este lote pode ser usado?". A cor que sai
 * na tela é a certa; o nome da classe é emprestado. O conserto de verdade é o
 * `Selo` ganhar os tons de material ligados aos `--status-*`, o que exige
 * mexer em `ui.css` — fora do alcance desta correção. Está nas pendências de
 * `docs/frontend/README.md`.
 *
 * <p>⚠️ **`BLOQUEADO` e `CONSUMIDO` saem com a mesma cor**, pela mesma falta de
 * tom. Não induz decisão errada — nenhum dos dois libera material, e o texto do
 * selo separa os dois —, mas apaga a diferença entre "está na prateleira e não
 * pode ser tocado" e "acabou". Também está nas pendências.
 */
export const TOM_DA_SITUACAO: Record<SituacaoDoLote, Situacao> = {
  QUARENTENA: "vencendo",
  APROVADO: "vigente",
  REPROVADO: "vencido",
  BLOQUEADO: "isento",
  CONSUMIDO: "isento",
};

/* ⚠️ `faixaDaEtiqueta` FOI REMOVIDA em 2026-08-13, junto com a faixa preta de
   situação da etiqueta impressa — não está "sem uso", está EXTINTA. A etiqueta
   não é trocada quando o lote é liberado, e uma etiqueta que diz QUARENTENA e
   nunca é trocada vira afirmação falsa no dia da aprovação. Ninguém recolhe
   vinte tambores para trocar adesivo. Quem informa a situação é o sistema (o
   QR abre o lote) e o controle no chão é a ÁREA FÍSICA DE QUARENTENA
   SEPARADA. Ver docs/backend/producao.md, "A etiqueta identifica; ela não
   decide". Ressuscitá-la é reabrir essa decisão, não reaproveitar código. */
